import React, { useEffect, useState } from "react";
import wordpressIcon from "../../../../assets/images/wordpress.svg";
import hubspotIcon from "../../../../assets/images/hubspot.svg";
import drupalIcon from "../../../../assets/images/drupalIcon.svg";
import geocoderIcon from "../../../../assets/images/geocoderIcon.svg";
import rocketIcon from "../../../../assets/images/rocketIcon.svg";
import infoIcon from "../../../../assets/images/infoIcon.svg";
import uploadIcon from "../../../../assets/images/uploadIcon.svg";
import cloudIcon from "../../../../assets/images/cloudIcon.svg";
import primaryIcon from "../../../../assets/images/primaryIcon.svg";
import linkIcon from "../../../../assets/images/linkIcon.svg";
import listIcon from "../../../../assets/images/listIcon.svg";
import diskIcon from "../../../../assets/images/diskIcon.svg";
import gearsIcon from "../../../../assets/images/gearsIcon.svg";
import trashIconRed from "../../../../assets/images/trashIcon-red.svg";
import trashIcon from "../../../../assets/images/trashIcon.svg";
import iconXML from "../../../../assets/images/iconXML.svg";
import loader from "../../../../assets/images/loader.gif";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  deletePrimaryImage,
  // getDomainsSelect,
  postConfirmSplitFile,
  postPrimaryImage,
  postProject,
  postSeedPageFile,
  postTestProject,
  cancelProject,
} from "../../../../utils/api";
import { useAuth } from "../../../../hooks/useAuth";
import Fancybox from "../Fancybox";
import { useNavigate } from "react-router-dom";
import Modal from "../../../../components/Modal";
import ConfirmModal from "../../../../components/ConfirmModal";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import wix from "../../../../assets/images/wixcom-logo.png";

const projectTypes = {
  Geocoder: geocoderIcon,
  WordPress: wordpressIcon,
  HubSpot: hubspotIcon,
  Drupal: drupalIcon,
  "Wix": wix,
};

const validationSchema = Yup.object()
  .shape({
    title: Yup.string().required("Title is required"),
    notes: Yup.string(),
    google_spread_sheet: Yup.string().url("Enter a valid URL"),
    script: Yup.string(),
    footer_links: Yup.string(),
    uploadedSeedPage: Yup.mixed(), //.required("Seed Page is required"),
  })
  .test("oneOfRequired", null, function (obj) {
    if (!obj.google_spread_sheet && !obj.script) {
      return this.createError({
        path: "google_spread_sheet",
        message:
          "Either Google Sheet Link or Manual Spreadsheet Data is required",
      });
    }
    return true;
  });

// Function to format file size with appropriate units
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  if (bytes < 1024 * 1024) {
    // If less than 1MB, show in KB
    return `${(bytes / 1024).toFixed(2)} KB`;
  } else {
    // Otherwise show in MB
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
};

const CreateProjectForm = () => {
  const { accessToken } = useAuth();
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedPrimaryImages, setUploadedPrimaryImages] = useState([]);
  const [selectedProjectType, setSelectedProjectType] = useState(
    JSON.parse(localStorage.getItem("selectedProjectType"))
  );
  const [uploading, setUploading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPrimaryImage, setUploadingPrimaryImage] = useState(false);
  const [domains, setDomains] = useState([]);
  const navigate = useNavigate();
  const [modalMessage, setModalMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [formData, setFormData] = useState([]);
  const [seedPageValue, setSeedPageValue] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState(null);

  // Call cancelProject when the component unmounts or when the user navigates away
  useEffect(() => {
    // Handle browser back button or page refresh
    const handleBeforeUnload = () => {
      if (accessToken) {
        cancelProject(accessToken).catch(err => console.error("Error canceling project:", err));
      }
    };
    
    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Cleanup function to call when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (accessToken) {
        cancelProject(accessToken).catch(err => console.error("Error canceling project:", err));
      }
    };
  }, [accessToken]);

  const handleProjectTypeChange = (type, formik) => {
    setSelectedProjectType(type);
    localStorage.setItem("selectedProjectType", JSON.stringify(type));
    
    // Reset file-related state when changing project type
    setUploadedImages([]);
    setUploadedPrimaryImages([]);
    setSeedPageValue(null);
    setFileError(null);
    
    // Reset form values to initial state
    if (formik && typeof formik.resetForm === 'function') {
      formik.resetForm({
        values: {
          ...formik.initialValues,
          selectedProjectType: type.name,
        }
      });
    }
  };

  const handleFileDrop = (e, setterFunction, type = "file") => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    
    if (type === "file") {
      // For seed page/collection (single file)
      if (selectedProjectType?.name === "Wix") {
        // For Wix projects, check for CSV files
        if (files[0].type !== "text/csv" && files[0].name.split('.').pop().toLowerCase() !== "csv") {
          setFileError("Only CSV files are allowed for Wix projects");
          return;
        }
      } else {
        // For other project types, check for XML files
        if (files[0].type !== "text/xml" && files[0].name.split('.').pop().toLowerCase() !== "xml") {
          setFileError("Only XML files are allowed");
          return;
        }
      }
      
      setFileError(null); // Clear any previous errors
      setterFunction("uploadedSeedPage", files[0]);
      setSeedPageValue(files[0].name); // Update the file name display
      
      // Also handle the file upload logic here
      if (files[0]) {
        setUploading(true);
        
        postSeedPageFile(accessToken, {
          file: files[0],
          type: selectedProjectType?.name,
        })
          .then(response => {
            setUploadedImages(response.data.data);
            setUploadedPrimaryImages(response.data.data);
          })
          .catch(error => {
            console.error("Error uploading file:", error);
            setFileError("Error uploading file. Please try again.");
          })
          .finally(() => {
            setUploading(false);
          });
      }
    } else if (type === "image") {
      // For images (multiple files)
      const imageFiles = Array.from(files).filter(file => 
        file.type.startsWith("image/") && file.size <= 5 * 1024 * 1024
      );
      
      if (imageFiles.length > 0) {
        // Handle image upload logic
        setUploadingPrimaryImage(true);
        
        // Process each image file
        const uploadPromises = imageFiles.map(file => {
          return postPrimaryImage(accessToken, {
            file,
            type: selectedProjectType.name,
          });
        });
        
        // Wait for all uploads to complete
        Promise.all(uploadPromises)
          .then(responses => {
            // Get only the new images from the responses
            const newImages = responses.reduce((acc, response) => {
              return [...acc, ...response.data.data];
            }, []);
            
            // Replace the existing images with the new ones instead of appending
            setUploadedImages(newImages);
            setUploadedPrimaryImages(newImages);
          })
          .catch(error => {
            console.error("Error uploading images:", error);
          })
          .finally(() => {
            setUploadingPrimaryImage(false);
          });
      }
    }
  };

  // useEffect(() => {
  //   if (!accessToken) return;
  //   getDomainsSelect(accessToken).then((res) => {
  //     setDomains(res.data.data);
  //   });
  // }, [accessToken]);

  const handleFileSelect = async (event, setFieldValue, type) => {
    if (type === "file") {
      const file = event.target.files[0];
      if (file) {
        // Check file type based on project type
        if (selectedProjectType?.name === "Wix") {
          // For Wix projects, check for CSV files
          if (file.type !== "text/csv" && file.name.split('.').pop().toLowerCase() !== "csv") {
            setFileError("Only CSV files are allowed for Wix projects");
            return;
          }
        } else {
          // For other project types, check for XML files
          if (file.type !== "text/xml" && file.name.split('.').pop().toLowerCase() !== "xml") {
            setFileError("Only XML files are allowed");
            return;
          }
        }
        
        setFileError(null);
        setUploading(true);

        try {
          const response = await postSeedPageFile(accessToken, {
            file,
            type: selectedProjectType?.name,
          });

          setFieldValue("uploadedSeedPage", file);
          setUploadedImages(response.data.data);
          setUploadedPrimaryImages(response.data.data);
          console.log("File size in bytes:", file.size);
        } catch (error) {
          console.error("Error uploading file:", error);
          setFileError("Error uploading file. Please try again.");
        } finally {
          setUploading(false);
        }
      }
    } else if (type === "image") {
      const files = event.target.files;
      if (files && files.length > 0) {
        setUploadingPrimaryImage(true);
        
        // Convert FileList to array and filter for images
        const imageFiles = Array.from(files).filter(file => 
          file.type.startsWith("image/") && file.size <= 5 * 1024 * 1024
        );
        
        try {
          // Process each image file
          const uploadPromises = imageFiles.map(file => {
            return postPrimaryImage(accessToken, {
              file,
              type: selectedProjectType.name,
            });
          });
          
          // Wait for all uploads to complete
          const responses = await Promise.all(uploadPromises);
          
          // Get only the new images from the responses
          const newImages = responses.reduce((acc, response) => {
            return [...acc, ...response.data.data];
          }, []);
          
          // Replace the existing images with the new ones
          setUploadedImages(newImages);
          setUploadedPrimaryImages(newImages);
        } catch (error) {
          console.error("Error uploading images:", error);
        } finally {
          setUploadingPrimaryImage(false);
        }
      }
    }
  };

  const handleDeletePrimaryImage = (url) => {
    deletePrimaryImage(
      accessToken,
      url,
      selectedProjectType?.name.toLowerCase(),
    ).then((res) => {
      const updatedImages = uploadedPrimaryImages.filter(
        (file) => file.url !== url,
      );
      setUploadedPrimaryImages(updatedImages);
    });
  };

  const handleDeleteImage = (url) => {
    // deleteImage(accessToken, url).then((res) => {
    //   const updatedImages = uploadedImages.filter((file) => file.url !== url);
    //   setUploadedImages(updatedImages);
    // });
  };

  const handleSubmit = (values, { setSubmitting }) => {
    console.log("handleSubmit with submit_type:", values.submit_type);
    
    if (values.submit_type === "save") {
      postProject(accessToken, values)
        .then((res) => {
          if (res.data.data.question_is_split_file) {
            setFormData(values);
            setIsConfirmModalOpen(true);
            setProjectId(res.data.data.project_id);
          } else {
            setIsError(false);
            setModalMessage("Project was successfully added!");
            setIsModalOpen(true);
            setTimeout(() => {
              navigate(`/projects/${res.data.data.project_id}`);
            }, 4000);
          }
        })
        .catch((err) => {
          setModalMessage(err.response.data.message);
          setIsError(true);
          setIsModalOpen(true);
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else if (values.submit_type === "test") {
      console.log("Calling postTestProject API");
      
      // Create the test payload with all necessary values
      const testPayload = {
        project_id: projectId, // Use projectId if available
        type: values.selectedProjectType.toLowerCase(),
        title: values.title,
        notes: values.notes || "",
        google_spread_sheet: values.google_spread_sheet || "",
        script: values.script || "",
        footer_links: values.footer_links || "",
        domain_id: values.domain || ""
      };
      
      console.log("Test payload:", testPayload);
      
      // Call the test API
      postTestProject(accessToken, testPayload)
        .then((res) => {
          console.log("Test API response:", res);
          setIsError(false);
          setModalMessage("Test was successfully started!");
          setIsModalOpen(true);
          
          // Navigate to the project page after showing the message
          setTimeout(() => {
            if (projectId) {
              navigate(`/projects/${projectId}`);
            } else if (res.data.data.project_id) {
              navigate(`/projects/${res.data.data.project_id}`);
            } else {
              navigate('/projects');
            }
          }, 2000);
        })
        .catch((err) => {
          console.error("Test API error:", err);
          setModalMessage(err.response?.data?.message || "An error occurred");
          setIsError(true);
          setIsModalOpen(true);
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const handleConfirm = (values) => {
    setLoading(true);
    postConfirmSplitFile(accessToken, { ...values, ...formData })
      .then((res) => {
        setIsConfirmModalOpen(false);
        setIsError(false);
        setLoading(false);
        setModalMessage("Project was successfully added!");
        setIsConfirmModalOpen(false);
        setIsModalOpen(true);
        setTimeout(() => {
          navigate(`/projects/${res.data.data.project_id}`);
        }, 4000);
      })
      .catch((err) => {
        setIsConfirmModalOpen(false);
        setModalMessage(err.response.data.message);
        setIsError(true);
        setLoading(false);
        setIsModalOpen(true);
      });
  };

  const handleCancel = () => {
    // Simply call the API and navigate
    cancelProject(accessToken)
      .catch(err => console.error("Error canceling project:", err))
      .finally(() => {
        navigate("/projects");
      });
  };

  return (
    <Formik
      initialValues={{
        selectedProjectType: selectedProjectType?.name || "WordPress",
        title: "",
        notes: "",
        site_base_url: "",
        domain: "",
        uploadedImage: null,
        google_spread_sheet: "",
        script: "",
        footer_links:
          "<a href='/geourl' title='INSERT SERVICE Geo City'>Geo City</a><br>",
        uploadedSeedPage: null,
        submit_type: "",
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        setFieldValue,
        handleChange,
        handleSubmit,
        isSubmitting,
        resetForm,
      }) => (
        <Form
          className="bg-[#F7F7F7] calc-h-header createProjectForm"
          onSubmit={handleSubmit}
        >
          <div className="p-8">
            <div className="flex gap-[15px] lg:gap-0 flex-col lg:flex-row lg:items-center justify-between">
              <div>
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold">Create Project</h1>
                  <div className="flex items-center ml-[12px]">
                    <img
                      src={projectTypes[values.selectedProjectType]}
                      alt={`${values.selectedProjectType} Icon`}
                      className="w-10 h-10 mr-3"
                    />
                  </div>
                </div>
                <p className="text-[14px] text-[#747474]">
                  Enter your project information below
                </p>
              </div>
              <div className="border border-[#E0E0E0] rounded-[10px] bg-white p-[15px] flex items-center w-fit">
                <div>
                  <h4 className="text-[14px] text-black font-bold">
                    Project Type
                  </h4>
                  <p className="mb-0 text-[10px] text-[#8C8C8C]">
                    Switch between project types
                  </p>
                </div>
                <div className="flex items-center space-x-4 ml-[14px]">
                  {Object.keys(projectTypes).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        handleProjectTypeChange({ 
                          id: Object.keys(projectTypes).indexOf(type) + 1, 
                          name: type, 
                          logo: projectTypes[type] 
                        }, {
                          resetForm,
                          initialValues: {
                            selectedProjectType: type,
                            title: "",
                            notes: "",
                            site_base_url: "",
                            domain: "",
                            uploadedImage: null,
                            google_spread_sheet: "",
                            script: "",
                            footer_links: "<a href='/geourl' title='INSERT SERVICE Geo City'>Geo City</a><br>",
                            uploadedSeedPage: null,
                            submit_type: "",
                          }
                        });
                        setFieldValue("selectedProjectType", type);
                      }}
                      className={`px-4 py-2 border rounded-lg w-[85px] h-[50px] ${
                        values.selectedProjectType === type
                          ? "border-orange-500 border"
                          : "border-gray-300"
                      }`}
                    >
                      <img
                        src={projectTypes[type]}
                        alt={`${type} Icon`}
                        className="inline"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex mt-[35px] flex-col lg:flex-row">
              <div className="w-full lg:w-[70%]">
                <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] gap-[22px]">
                  <div className="flex items-center mb-[22px]">
                    <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                      <img
                        src={rocketIcon}
                        alt="Detail Icon"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <h2 className="text-[18px] text-black font-semibold flex items-center">
                        Project Details
                      </h2>
                      <p className="text-[12px] text-[#8C8C8C]">
                        Name your project and add any additional information for
                        internal purposes.
                      </p>
                    </div>
                  </div>
                  <div className="mb-[22px]">
                    <label
                      htmlFor="title"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Title
                      <Tooltip tooltip="This will be the title of your project">
                        <img src={infoIcon} alt="Info" className="ml-1" />
                      </Tooltip>
                    </label>
                    <Field
                      type="text"
                      id="title"
                      name="title"
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>
                  {values.selectedProjectType === "Drupal" && (
                    <div className="mb-[22px]">
                      <label
                        htmlFor="site-base-url"
                        className="flex text-[12px] font-[600] text-[#001522]"
                      >
                        Site base URL
                        <Tooltip tooltip="This will be the Site base URL of your project">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </label>
                      <Field
                        type="text"
                        id="site-base-url"
                        name="site-base-url"
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      />
                      <ErrorMessage
                        name="site-base-url"
                        component="div"
                        className="text-red-500 text-xs"
                      />
                    </div>
                  )}
                  <div className="flex">
                    {values.selectedProjectType !== "Geocoder" && (
                      <div className="w-full mr-[22px]">
                        <label
                          htmlFor="notes"
                          className="flex text-[12px] font-[600] text-[#001522]"
                        >
                          Notes
                          <Tooltip tooltip="Name and add any notes you have regarding your project below">
                            <img src={infoIcon} alt="Info" className="ml-1" />
                          </Tooltip>
                        </label>
                        <Field
                          as="textarea"
                          id="notes"
                          name="notes"
                          rows={4}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        />
                        <ErrorMessage
                          name="notes"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>
                    )}

                    {/* Domains */}
                    {/* <div className="w-[30%]">
                      <label
                        htmlFor="domain"
                        className="flex text-[12px] font-[600] text-[#001522]"
                      >
                        Domain
                        <Tooltip tooltip="Domain">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </label>
                      <Field
                        as="select"
                        id="domain"
                        name="domain"
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        onChange={(e) => {
                          handleChange(e);

                          if (e.target.value === "add_new") {
                            navigate("/add-domain");
                          }
                        }}
                      >
                        {[{ name: "", id: null }, ...domains]?.map(
                          (d, index) => (
                            <option key={index} value={d.id}>
                              {d.name}
                            </option>
                          ),
                        )}
                        <option
                          value="add_new"
                          className="text-[#747474]"
                          onClick={() => navigate("/add-domain")}
                        >
                          + Add New
                        </option>
                      </Field>
                      <ErrorMessage
                        name="domain"
                        component="div"
                        className="text-red-500 text-xs"
                      />
                    </div> */}
                  </div>
                </div>

                {values.selectedProjectType === "Geocoder" && (
                  <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] mt-[30px]">
                    <div>
                      <div className="flex items-center mb-[22px]">
                        <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                          <img
                            src={uploadIcon}
                            alt="Upload Icon"
                            className="w-full"
                          />
                        </div>
                        <div>
                          <h2 className="text-[18px] text-black font-semibold flex items-center">
                            Upload Images
                            <Tooltip tooltip="Click or drag and drop to upload your Image file">
                              <img src={infoIcon} alt="Info" className="ml-1" />
                            </Tooltip>
                          </h2>
                          <p className="text-[12px] text-[#8C8C8C]">
                            Click or drag and drop to upload your Image file.
                          </p>
                        </div>
                      </div>
                      <div
                        className={`border-dashed border-2 border-gray-300 p-4 text-center flex flex-col ${values.uploadedImage ? "items-start" : "items-center"} rounded-[6px]`}
                        onDrop={(e) => handleFileDrop(e, setUploadedImages, "image")}
                        onDragOver={(e) => e.preventDefault()}
                        id="uploadedImage"
                      >
                        {uploading ? (
                          <div>Loading...</div>
                        ) : values.uploadedImage ? (
                          <div className="flex items-center">
                            <div className="text-center">
                              <img src={iconXML} alt="icon XML" />
                              <span className="text-[#8C8C8C] text-[10px] font-[600]">
                                {formatFileSize(values.uploadedImage?.size)}
                              </span>
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                setFieldValue("uploadedImage", null)
                              }
                              className="flex text-[#E80000] border border-[#E80000] text-[10px] font-[600] rounded-[5px] px-[13px] py-[8px] ml-[35px]"
                            >
                              <img src={trashIconRed} alt="Trash Icon" />
                              Remove Image
                            </button>
                          </div>
                        ) : (
                          <>
                            <img
                              src={cloudIcon}
                              alt="Cloud"
                              className="max-w-[31px] mb-2"
                            />
                            <p className="text-[13px] text-black font-bold">
                              Drag and Drop imaage here
                            </p>
                            <p className="text-[10px] text-[#8C8C8C]">or</p>
                            <input
                              type="file"
                              id="fileInput"
                              multiple
                              className="hidden"
                              onChange={(e) =>
                                handleFileSelect(e, setUploadedImages, "image")
                              }
                            />
                            <label
                              htmlFor="fileInput"
                              className="inline-block mt-2 px-[8px] py-[3px] bg-gradient text-white text-[8px] font-semibold rounded-[5px] cursor-pointer"
                            >
                              Browse Files
                            </label>
                            <p className="text-[7px] text-[#8C8C8C] font-[400] mt-2">
                              Max file size: 5 MB per image
                            </p>
                            <div>
                              <Fancybox
                                options={{
                                  Carousel: {
                                    infinite: false,
                                  },
                                }}
                              >
                                {uploadedImages?.map((file, index) => (
                                  <div
                                    className="flex flex-col justify-center w-[46%]"
                                    key={index}
                                  >
                                    <div className="primaryImage">
                                      <img
                                        src={file.url}
                                        alt=""
                                        className="max-w-[154px] max-h-[88px] w-full photo"
                                      />
                                      <div className="wrapper-icons flex justify-center gap-[40px]">
                                        <div>
                                          <a
                                            key={index}
                                            data-fancybox="gallery"
                                            href={file.url}
                                            className="flex items-center flex-col gap-[4px]"
                                          >
                                            <span className="text-white text-[7px]">
                                              View
                                            </span>
                                          </a>
                                        </div>
                                        <div>
                                          <button
                                            type="button"
                                            className="flex items-center flex-col gap-[4px]"
                                            onClick={() =>
                                              handleDeleteImage(file.url)
                                            }
                                          >
                                            <img
                                              src={trashIcon}
                                              alt="trash Icon"
                                            />
                                            <span className="text-white text-[7px]">
                                              Remove
                                            </span>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                    <span className="text-[#6C6D74] text-[8px] max-w-[100px] whitespace-nowrap overflow-hidden overflow-ellipsis pt-[5px] my-0 mx-auto">
                                      {file.fileName}
                                    </span>
                                  </div>
                                ))}
                                {uploadingImage && (
                                  <div className="absolute top-0 left-0 w-full h-full bg-gray-400 opacity-[0.5] flex items-center justify-center rounded-[4px]">
                                    <img
                                      src={loader}
                                      alt="Loader"
                                      className="w-[100px] h-[100px]"
                                    />
                                  </div>
                                )}
                              </Fancybox>
                            </div>
                            {values.uploadedImage && (
                              <ul className="mt-4 text-sm text-gray-700">
                                <li>{values.uploadedImage[0].name}</li>
                              </ul>
                            )}
                            <p className="text-[7px] text-[#8C8C8C] font-[400] mt-2">
                              Supports: JPEG
                            </p>
                          </>
                        )}
                      </div>
                      {!uploadedImages && (
                        <ErrorMessage
                          name="uploadedImage"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      )}
                    </div>
                  </div>
                )}

                {values.selectedProjectType !== "Geocoder" && (
                  <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] mt-[30px]">
                    <div>
                      <div className="flex items-center mb-[22px]">
                        <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                          <img
                            src={uploadIcon}
                            alt="Upload Icon"
                            className="w-full"
                          />
                        </div>
                        <div>
                          <h2 className=" text-black font-semibold flex items-center">
                            <span className="text-[18px] text-black">
                              {selectedProjectType?.name === "Wix" ? "Upload Seed Collection" : "Upload Seed Page"}
                            </span>

                            <Tooltip tooltip={selectedProjectType?.name === "Wix" 
                              ? "Export the seed collection from Wix and upload it below" 
                              : "Export the seed page from your content management system and upload it below"}>
                              <img src={infoIcon} alt="Info" className="ml-1" />
                            </Tooltip>
                          </h2>
                          <p className="text-[12px] text-[#8C8C8C]">
                            {selectedProjectType?.name === "Wix" 
                              ? "Click or drag and drop to upload your CSV file containing the seed collection." 
                              : "Click or drag and drop to upload your XML file containing the seed page."}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`border-dashed border-2 border-gray-300 p-4 text-center flex flex-col ${values.uploadedSeedPage ? "items-start" : "items-center"} rounded-[6px]`}
                        onDrop={(e) => handleFileDrop(e, setFieldValue, "file")}
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnter={(e) => e.preventDefault()}
                        id="uploadedSeedPage"
                      >
                        {fileError && (
                          <div className="text-red-500 text-sm mb-2">{fileError}</div>
                        )}
                        
                        {uploading ? (
                          <div>Loading...</div>
                        ) : values.uploadedSeedPage ? (
                          <div className="flex items-center">
                            <div className="text-center">
                              <img src={iconXML} alt="icon XML" />
                              <span className="text-[#8C8C8C] text-[10px] font-[600]">
                                {formatFileSize(values.uploadedSeedPage?.size)}
                              </span>
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                setFieldValue("uploadedSeedPage", null)
                              }
                              className="flex text-[#E80000] border border-[#E80000] text-[10px] font-[600] rounded-[5px] px-[13px] py-[8px] ml-[35px]"
                            >
                              <img src={trashIconRed} alt="Trash Icon" />
                              Remove File
                            </button>
                          </div>
                        ) : (
                          <>
                            <img
                              src={cloudIcon}
                              alt="Cloud"
                              className="max-w-[31px] mb-2"
                            />
                            <p className="text-[13px] text-black font-bold">
                              Drag and Drop file here
                            </p>
                            <p className="text-[10px] text-[#8C8C8C]">or</p>
                            <input
                              type="file"
                              accept={selectedProjectType?.name === "Wix" ? ".csv" : ".xml"}
                              className="hidden"
                              id="selectXML"
                              onChange={(e) => {
                                handleFileSelect(e, setFieldValue, "file");
                                setSeedPageValue(e.target.value);
                                console.log("seedPageValue", e.target.value);
                              }}
                            />
                            <label
                              htmlFor="selectXML"
                              className="inline-block mt-2 px-[8px] py-[3px] bg-gradient text-white text-[8px] font-semibold rounded-[5px] cursor-pointer"
                            >
                              Browse Files
                            </label>
                            {values.uploadedSeedPage && (
                              <ul className="mt-4 text-sm text-gray-700">
                                <li>{values.uploadedSeedPage[0].name}</li>
                              </ul>
                            )}
                            <p className="text-[7px] text-[#8C8C8C] font-[400] mt-2">
                              Supports: {selectedProjectType?.name === "Wix" ? "CSV" : "XML"}
                            </p>
                          </>
                        )}
                      </div>
                      {!seedPageValue && (
                        <ErrorMessage
                          name="uploadedSeedPage"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] mt-[30px]">
                  <div className="flex items-center mb-[22px]">
                    <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                      <img
                        src={listIcon}
                        alt="Spreadsheet Icon"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <h2 className="text-[18px] text-black font-semibold flex items-center">
                        Import Spreadsheet Data
                        <Tooltip tooltip="Import Spreadsheet Data">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </h2>
                      <p className="text-[12px] text-[#8C8C8C]">
                        Click or drag and drop to upload your XML file
                        containing the seed page.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="google_spread_sheet"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Google Sheet Link
                      <Tooltip tooltip="Google Sheet Link">
                        <img src={infoIcon} alt="Info" className="ml-1" />
                      </Tooltip>
                    </label>
                    <Field
                      type="text"
                      id="google_spread_sheet"
                      name="google_spread_sheet"
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                    <ErrorMessage
                      name="google_spread_sheet"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>
                  <div className="flex items-center my-[10px]">
                    <div className="w-[23px] bg-[#6C6D74] h-[1px]"></div>
                    <span className="mx-[7px] text-[#6C6D74] text-[10px] font-[600]">
                      OR
                    </span>
                    <div className="w-[23px] bg-[#6C6D74] h-[1px]"></div>
                  </div>
                  <div className="mb-[22px]">
                    <label
                      htmlFor="script"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Manually Paste Data From Spreadsheet
                      <Tooltip tooltip="Manually Paste Data From Spreadsheet">
                        <img src={infoIcon} alt="Info" className="ml-1" />
                      </Tooltip>
                    </label>
                    <Field
                      as="textarea"
                      id="script"
                      name="script"
                      rows={4}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                  </div>
                </div>

                {values.selectedProjectType !== "Geocoder" && (
                  <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] mt-[30px]">
                    <div className="flex items-center mb-[22px]">
                      <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                        <img
                          src={linkIcon}
                          alt="Link Icon"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <h2 className="text-[18px] text-black font-semibold flex items-center">
                          Footer Link Structure
                          <Tooltip tooltip="Footer Link Structure">
                            <img src={infoIcon} alt="Info" className="ml-1" />
                          </Tooltip>
                        </h2>
                        <p className="text-[12px] text-[#8C8C8C]">
                          Specify how you would like your footer links to be
                          structured
                        </p>
                      </div>
                    </div>
                    <div className="mb-[22px]">
                      <label
                        htmlFor="footer_links"
                        className="flex text-[12px] font-[600] text-[#001522]"
                      >
                        Link Structure
                        <Tooltip tooltip="Link Structure">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </label>
                      <Field
                        type="text"
                        id="footer_links"
                        name="footer_links"
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
              {values.selectedProjectType !== "Geocoder" && (
                <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] w-full max-w-[375px] mt-[30px] lg:mt-[0] h-fit">
                  <div className="flex items-center mb-[22px]">
                    <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                      <img
                        src={primaryIcon}
                        alt="Primary Icon"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <h2 className="text-[18px] text-black font-semibold flex items-center">
                        Primary Images
                        <Tooltip tooltip="Primary Images">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </h2>
                      <p className="text-[12px] text-[#8C8C8C]">
                        Upload additional images for geo processing
                      </p>
                    </div>
                  </div>

                  <div
                    className="border-dashed rounded-[6px] border-2 border-gray-300 p-4 text-center flex flex-col items-center mb-[27px]"
                    onDrop={(e) => handleFileDrop(e, setFieldValue, "image")}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={(e) => e.preventDefault()}
                  >
                    <img
                      src={cloudIcon}
                      alt="Cloud"
                      className="max-w-[31px] mb-2"
                    />
                    <p className="text-[13px] text-black font-bold">
                      Drag and Drop files here
                    </p>
                    <p className="text-[10px] text-[#8C8C8C]">or</p>

                    <input
                      type="file"
                      id="fileInput"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileSelect(e, setFieldValue, "image")}
                    />

                    <label
                      htmlFor="fileInput"
                      className="inline-block mt-2 px-[8px] py-[3px] bg-gradient text-white text-[8px] font-semibold rounded-[5px] cursor-pointer"
                    >
                      Browse Files
                    </label>

                    <p className="text-[7px] text-[#8C8C8C] font-[400] mt-2">
                      Max file size: 5 MB per image
                    </p>
                  </div>
                  <div>
                    <Fancybox
                      options={{
                        Carousel: {
                          infinite: false,
                        },
                      }}
                    >
                      {uploadedPrimaryImages?.map((file, index) => (
                        <div
                          className="flex flex-col justify-center w-[46%]"
                          key={index}
                        >
                          <div className="primaryImage">
                            <img
                              src={file.url}
                              alt=""
                              className="max-w-[154px] max-h-[88px] w-full photo"
                            />
                            <div className="wrapper-icons flex justify-center gap-[40px]">
                              <div>
                                <a
                                  key={index}
                                  data-fancybox="gallery"
                                  href={file.url}
                                  className="flex items-center flex-col gap-[4px]"
                                >
                                  <span className="text-white text-[7px]">
                                    View
                                  </span>
                                </a>
                              </div>
                              <div>
                                <button
                                  type="button"
                                  className="flex items-center flex-col gap-[4px]"
                                  onClick={() =>
                                    handleDeletePrimaryImage(file.url)
                                  }
                                >
                                  <img src={trashIcon} alt="trash Icon" />
                                  <span className="text-white text-[7px]">
                                    Remove
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                          <span className="text-[#6C6D74] text-[8px] max-w-[100px] whitespace-nowrap overflow-hidden overflow-ellipsis pt-[5px] my-0 mx-auto">
                            {file.fileName}
                          </span>
                        </div>
                      ))}
                      {uploadingPrimaryImage && (
                        <div className="absolute top-0 left-0 w-full h-full bg-gray-400 opacity-[0.5] flex items-center justify-center rounded-[4px]">
                          <img
                            src={loader}
                            alt="Loader"
                            className="w-[100px] h-[100px]"
                          />
                        </div>
                      )}
                    </Fancybox>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="sticky w-full bottom-0 left-0 flex justify-between bg-white border-t border-t-[#E0E0E0] pt-5 px-9">
            <div className="flex">
              <button
                type="submit"
                onClick={() => setFieldValue("submit_type", "save")}
                className={`flex flex-wrap bg-gradient-to-r from-[#fe6432] to-[#f8761f] rounded-[5px] text-white text-[10px] font-[600] ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""} px-[13px] py-[8px] mr-[13px]`}
                disabled={isSubmitting}
              >
                {!isSubmitting && (
                  <img src={diskIcon} alt="Disk Icon" className="mr-[5px]" />
                )}
                {isSubmitting ? "Saving..." : "Save Project"}
              </button>
              <button
                type="submit"
                onClick={() => {
                  console.log("Test Page button clicked");
                  setFieldValue("submit_type", "test");
                }}
                className={`flex text-[#001522] border border-[#000000] text-[10px] font-[600] ${
                  isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                } rounded-[5px] px-[13px] py-[8px]`}
                disabled={isSubmitting}
              >
                <img src={gearsIcon} alt="Gears Icon" className="mr-[5px]" />
                {isSubmitting && values.submit_type === "test" ? "Testing..." : "Test Page"}
              </button>
            </div>
            <div>
              <button
                onClick={handleCancel}
                type="button"
                className="flex text-[#E80000] border border-[#E80000] text-[10px] font-[600] rounded-[5px] px-[13px] py-[8px]"
              >
                <img src={trashIconRed} alt="Trash Icon" className="mr-[5px]" />
                Cancel
              </button>
            </div>

            {isModalOpen && (
              <Modal
                message={modalMessage}
                isError={isError}
                onClose={() => setIsModalOpen(false)}
              />
            )}

            {isConfirmModalOpen && (
              <ConfirmModal
                message="Your output XML file is larger than 10MB.
                      Would you like us to split it into multiple files for an easier upload?"
                onConfirm={() => handleConfirm({ split_xml_file: 2 })}
                onCancel={() => handleConfirm({ split_xml_file: 0 })}
                loading={loading}
              />
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default CreateProjectForm;
